import React, { useEffect, useRef, useState } from "react";
import Button from "../../shared/Button";
import { URL as url } from "../../../../../utils/Constants";
import swal from "sweetalert";
import { useAssessmentScale } from "../../../hooks/useAssessmentScale";
import { Spinner } from "reactstrap";
import { useARRCode } from "../../../context/ARRLevelContext";
import Select from "../../shared/Select";
import { Tooltip } from "@mui/material";

const levelTypeOption = [
  {
    label: "Alphabets",
    value: "alphabets",
  },
  {
    label: "Number",
    value: "number",
  },
  {
    label: "Text",
    value: "text",
  },
  {
    label: "Image",
    value: "image",
  },
  {
    label: "Colour stickers",
    value: "colourStickers",
  },
];

const LevelsAssessmentScale = ({
  gradeSelectOptions,
  type,
  scaleType,
  data,
  handleUpdateAssessment,
  updating,
  closeModal,
}) => {
  const { createAssessmentScale, createAssessmentScaleWithImage, loading } =
    useAssessmentScale();
  const { arrLevel } = useARRCode();
  const imagesRef = useRef([]);
  const [recordedIds, setRecordedIds] = useState([]);
  const [deletedRowIds, setDeletedRowIds] = useState([]);
  const [levelsAssessmentScale, setLevelsAssessmentScale] = useState({
    scaleName: "",
    levelName: "",
    levelTypes: "alphabets",
    gradeFormat: [
      {
        alphabets: "",
        number: "",
        text: "",
        colourStickers: "",
        image: null,
        description: "",
        descriptor: "",
      },
    ],
  });

  const isView = type === "view";

  const addNewRow = () => {
    setLevelsAssessmentScale((prev) => ({
      ...prev,
      gradeFormat: [
        ...prev.gradeFormat,
        {
          number: "",
          text: "",
          colourStickers: "",
          image: null,
          description: "",
          descriptor: "",
        },
      ],
    }));
  };

  const removeCurrentRow = (index, id) => {
    const newGradeFormat = levelsAssessmentScale?.gradeFormat?.filter(
      (item, i) => i !== index,
    );

    setLevelsAssessmentScale((prev) => ({
      ...prev,
      gradeFormat: newGradeFormat,
    }));

    setDeletedRowIds((prev) => [...prev, id]);
  };

  const removeImage = (index) => {
    const newGradeFormat = levelsAssessmentScale?.gradeFormat?.map((item, i) =>
      index === i ? { ...item, image: null } : item,
    );

    setLevelsAssessmentScale((prev) => ({
      ...prev,
      gradeFormat: newGradeFormat,
    }));
    return;
  };

  const getPreview = (file) => {
    if (!file) return "";

    if (typeof file === "string") {
      return url.imageSelectURL + file;
    }

    if (file instanceof File || file instanceof Blob) {
      return URL.createObjectURL(file);
    }

    return "";
  };

  const handleLevelAssessmentChange = (e, index) => {
    const { name, value, files } = e.target;

    if (files?.length > 0) {
      const file = files[0];

      if (file) {
        if (!file.type.startsWith("image/")) {
          swal("Oops", "Please upload a valid image file.");
          return;
        }
      }
    }

    if (index !== undefined) {
      const newGradeFormat = levelsAssessmentScale?.gradeFormat?.map(
        (item, i) =>
          index === i
            ? { ...item, [name]: files?.length > 0 ? files[0] : value }
            : item,
      );

      setLevelsAssessmentScale((prev) => ({
        ...prev,
        gradeFormat: newGradeFormat,
      }));

      return;
    }

    setLevelsAssessmentScale((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateNegativeNumber = (e) => {
    if (e.key === "-" || e.key === "Subtract" || e.key === "e") {
      e.preventDefault();
    }
  };

  const handleSubmit = async () => {
    if (levelsAssessmentScale.scaleName.trim() === "") {
      swal("Oops", "Scale Name is required");
      return;
    }

    if (levelsAssessmentScale.levelName.trim() === "") {
      swal("Oops", "Level Name is required");
      return;
    }

    if (levelsAssessmentScale?.gradeFormat?.length === 0) {
      swal("Oops", "At least one grade is required");
      return;
    }

    const typeKeyMap = {
      alphabets: "alphabets",
      number: "number",
      text: "text",
      image: "image",
      colourStickers: "colourStickers",
    };

    const key = typeKeyMap[levelsAssessmentScale?.levelTypes];

    const hasEmptyFields = levelsAssessmentScale?.gradeFormat.some((item) => {
      return (
        item.description.trim() === "" ||
        item.descriptor.trim() === "" ||
        (key === "image" ? !item[key] : !item[key]?.toString().trim())
      );
    });

    if (hasEmptyFields) {
      swal("Oops", "Please fill all the fields");
      return;
    }

    const levelType = levelsAssessmentScale?.levelTypes;

    if (type === "create" || type === "edit") {
      if (levelType === "image") {
        let formData = new FormData();

        if (levelsAssessmentScale?.gradeFormat) {
          levelsAssessmentScale.gradeFormat.forEach((item) => {
            if (item.image instanceof File) {
              formData.append("files", item.image);
            }
          });
        }

        if (type === "create") {
          const levels = levelsAssessmentScale?.gradeFormat?.map((item) => ({
            descriptor: item.descriptor,
            description: item.description,
            level: "",
            levelType: levelType,
          }));

          formData.append("levels", JSON.stringify(levels));

          const levelRecord = {
            assessmentScaleTypeCode: scaleType,
            assessmentModuleCode: arrLevel,
            scaleName: levelsAssessmentScale.scaleName,
            scoreName: levelsAssessmentScale.levelName,
          };

          formData.append("levelRecord", JSON.stringify(levelRecord));

          try {
            await createAssessmentScaleWithImage(formData);
            setLevelsAssessmentScale({
              scaleName: "",
              levelName: "",
              levelTypes: "alphabets",
              gradeFormat: [
                {
                  alphabets: "",
                  number: "",
                  text: "",
                  colourStickers: "",
                  image: null,
                  description: "",
                  descriptor: "",
                },
              ],
            });
          } catch (error) {
            console.error("Create Failed", error);
          }
        } else {
          const levels = levelsAssessmentScale?.gradeFormat?.map((item) => ({
            descriptor: item.descriptor,
            description: item.description,
            level: item?.image
              ? item?.image instanceof File
                ? `file:${item?.image?.name}`
                : item?.image
              : "",
            levelType: levelType,
            id: item?.id,
          }));

          formData.append("levels", JSON.stringify(levels));

          const levelRecord = {
            id: data.id,
            assessmentScaleTypeCode: data?.assessmentScaleTypeCode,
            assessmentModuleCode: arrLevel,
            scaleName: levelsAssessmentScale.scaleName,
            scoreName: levelsAssessmentScale.levelName,
            delete: deletedRowIds,
          };

          formData.append("levelRecord", JSON.stringify(levelRecord));

          handleUpdateAssessment(formData, true);
        }
      } else {
        if (type === "create") {
          const assessmentScaleLevels = levelsAssessmentScale?.gradeFormat?.map(
            (item) => ({
              descriptor: item.descriptor,
              description: item.description,
              levelType: levelsAssessmentScale.levelTypes,
              level: item[levelType],
              grade: item[levelType],
              changeColor:
                levelType === "colourStickers" ? item[levelType] : "",
            }),
          );

          const formData = {
            assessmentScaleTypeCode: scaleType,
            assessmentModuleCode: arrLevel,
            scaleName: levelsAssessmentScale.scaleName,
            scoreName: levelsAssessmentScale.levelName,
            assessmentScaleLevels: assessmentScaleLevels,
          };

          try {
            await createAssessmentScale(formData);
            setLevelsAssessmentScale({
              scaleName: "",
              levelName: "",
              levelTypes: "alphabets",
              gradeFormat: [
                {
                  alphabets: "",
                  number: "",
                  text: "",
                  colourStickers: "",
                  image: null,
                  description: "",
                  descriptor: "",
                },
              ],
            });
          } catch (error) {
            console.error("Create Failed", error);
          }
        } else {
          const assessmentScaleLevels = levelsAssessmentScale?.gradeFormat?.map(
            (item) => ({
              id: item?.id,
              descriptor: item.descriptor,
              description: item.description,
              levelType: levelsAssessmentScale.levelTypes,
              level: item[levelType],
              grade: item[levelType],
              changeColor:
                levelType === "colourStickers" ? item[levelType] : "",
            }),
          );

          const formData = {
            id: data.id,
            assessmentScaleTypeCode: scaleType,
            assessmentModuleCode: arrLevel,
            scaleName: levelsAssessmentScale.scaleName,
            scoreName: levelsAssessmentScale.levelName,
            assessmentScaleLevels: assessmentScaleLevels,
            delete: deletedRowIds,
          };

          handleUpdateAssessment(formData);
        }
      }
    }
  };

  useEffect(() => {
    if ((type === "edit" || isView) && data) {
      const filteredData = data?.assessmentScaleLevels?.map((item) => ({
        alphabets: item?.levelType === "alphabets" ? item?.level : "",
        number: item?.levelType === "number" ? item?.level : "",
        text: item?.levelType === "text" ? item?.level : "",
        colourStickers: item?.levelType === "colourStickers" ? item?.level : "",
        image: item?.levelType === "image" ? item?.level : "",
        description: item?.description,
        descriptor: item?.descriptor,
        id: item?.id,
      }));

      setLevelsAssessmentScale({
        scaleName: data?.scaleName,
        levelName: data?.scoreName,
        levelTypes: data?.assessmentScaleLevels[0]?.levelType,
        gradeFormat: filteredData,
      });

      setRecordedIds(data?.recordedIds);
    }
  }, [type, data]);

  return (
    <>
      <div className={`arr-form grid ${type !== "create" ? "noShadow" : ""}`}>
        <div className="form-input">
          <label className="required" htmlFor="scaleName">
            Scale Name
          </label>
          <input
            className="form-control"
            type="text"
            value={levelsAssessmentScale.scaleName}
            onChange={(e) => handleLevelAssessmentChange(e)}
            name="scaleName"
            id="scaleName"
            readOnly={isView}
            disabled={isView}
          />
        </div>

        <div className="form-input">
          <label className="required" htmlFor="levelName">
            Level Name
          </label>
          <input
            className="form-control"
            type="text"
            value={levelsAssessmentScale.levelName}
            onChange={(e) => handleLevelAssessmentChange(e)}
            name="levelName"
            id="levelName"
            readOnly={isView}
            disabled={isView}
          />
        </div>

        {type === "create" && (
          <div className="form-input">
            <label htmlFor="levelTypes">Level Type</label>
            <Select
              name={"levelType"}
              value={levelTypeOption.find(
                (o) => o.value === levelsAssessmentScale.levelTypes,
              )}
              options={levelTypeOption}
              onChange={(option) => {
                if (isView) return;
                setLevelsAssessmentScale((prev) => ({
                  ...prev,
                  levelTypes: option?.value,
                  gradeFormat: [
                    {
                      number: "",
                      text: "",
                      colourStickers: "",
                      image: null,
                      description: "",
                      descriptor: "",
                    },
                  ],
                }));
              }}
              isClearable={false}
              isDisabled={isView}
            />
          </div>
        )}
      </div>

      <div className="arr-table-container">
        <table aria-label="arr-table" className="arr-table">
          <thead>
            <tr>
              <th>
                <span className="required">Grade</span>
              </th>
              <th>
                <span className="required">Descriptor</span>
              </th>
              <th>
                <span className="required">Description</span>
              </th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {levelsAssessmentScale?.gradeFormat?.length > 0 ? (
              levelsAssessmentScale?.gradeFormat?.map((item, index) => {
                const isRecorded =
                  item?.id != null &&
                  recordedIds?.length > 0 &&
                  recordedIds.includes(item.id);

                return (
                  <tr key={index}>
                    <td width={300}>
                      {levelsAssessmentScale?.levelTypes === "alphabets" && (
                        <Select
                          name={"levelAlphabets"}
                          options={gradeSelectOptions}
                          value={gradeSelectOptions?.find(
                            (o) => o?.value === item.alphabets,
                          )}
                          onChange={(option) => {
                            if (isView) return;
                            const newGradeFormat =
                              levelsAssessmentScale?.gradeFormat?.map(
                                (item, i) =>
                                  index === i
                                    ? { ...item, alphabets: option?.value }
                                    : item,
                              );
                            setLevelsAssessmentScale((prev) => ({
                              ...prev,
                              gradeFormat: newGradeFormat,
                            }));
                          }}
                          isClearable={false}
                          isDisabled={isView}
                        />
                      )}

                      {levelsAssessmentScale?.levelTypes === "number" && (
                        <input
                          aria-label="levelNumber"
                          className="form-control"
                          type="number"
                          name="number"
                          min={0}
                          value={item.number}
                          onKeyDown={validateNegativeNumber}
                          onChange={(e) =>
                            handleLevelAssessmentChange(e, index)
                          }
                          disabled={isView}
                          readOnly={isView}
                        />
                      )}

                      {levelsAssessmentScale?.levelTypes === "text" && (
                        <input
                          aria-label="levelText"
                          className="form-control"
                          type="text"
                          name="text"
                          value={item.text}
                          onChange={(e) =>
                            handleLevelAssessmentChange(e, index)
                          }
                          disabled={isView}
                          readOnly={isView}
                        />
                      )}

                      {levelsAssessmentScale?.levelTypes === "image" &&
                        (!item.image ? (
                          <div>
                            <input
                              ref={(el) => (imagesRef.current[index] = el)}
                              type="file"
                              aria-label="imageInput"
                              name="image"
                              onChange={(e) =>
                                handleLevelAssessmentChange(e, index)
                              }
                              accept=".jpg, .jpeg, .png, .webp"
                              hidden
                              disabled={isView}
                              readOnly={isView}
                            />
                            <Button
                              onClick={() => imagesRef.current[index].click()}
                            >
                              Image
                            </Button>
                          </div>
                        ) : (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                            }}
                          >
                            <img
                              width={56}
                              height={56}
                              alt="image"
                              src={getPreview(item?.image)}
                            />
                            {!isView && (
                              <Button
                                onClick={() => removeImage(index)}
                                size="sm"
                                variant="danger"
                              >
                                Remove Image
                              </Button>
                            )}
                          </div>
                        ))}

                      {levelsAssessmentScale?.levelTypes ===
                        "colourStickers" && (
                        <input
                          aria-label="levelColourStickers"
                          className="form-control"
                          type="color"
                          name="colourStickers"
                          value={item.colourStickers}
                          onChange={(e) =>
                            handleLevelAssessmentChange(e, index)
                          }
                          disabled={isView}
                          readOnly={isView}
                        />
                      )}
                    </td>

                    <td>
                      <input
                        aria-label="descriptor"
                        className="form-control"
                        type="text"
                        name="descriptor"
                        value={item.descriptor}
                        onChange={(e) => handleLevelAssessmentChange(e, index)}
                        disabled={isView}
                        readOnly={isView}
                      />
                    </td>

                    <td>
                      <input
                        aria-label="description"
                        className="form-control"
                        type="text"
                        name="description"
                        value={item.description}
                        onChange={(e) => handleLevelAssessmentChange(e, index)}
                        disabled={isView}
                        readOnly={isView}
                      />
                    </td>

                    <td>
                      {!isView && (
                        <Tooltip
                          title={
                            isRecorded
                              ? "You cannot remove it because this grade has already been recorded in the subject/general assessment."
                              : ""
                          }
                        >
                          <div>
                            <Button
                              size="sm"
                              onClick={() => removeCurrentRow(index, item?.id)}
                              variant="danger"
                              disabled={isRecorded}
                            >
                              Remove
                            </Button>
                          </div>
                        </Tooltip>
                      )}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td style={{ textAlign: "center" }} colSpan={4}>
                  No Data Found
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {!isView && (
          <div style={{ marginTop: "17px" }} className="action-btns end">
            <Button size="sm" onClick={addNewRow}>
              Add Row
            </Button>
          </div>
        )}
      </div>

      {!isView && (
        <div className="action-btns end" style={{ marginTop: "24px" }}>
          {type !== "create" && (
            <Button variant="outline" onClick={closeModal}>
              Cancel
            </Button>
          )}

          <Button disabled={loading.create || updating} onClick={handleSubmit}>
            {loading.create || updating ? (
              <Spinner size={"sm"} color="white" />
            ) : type === "create" ? (
              "Create Assessment Scale"
            ) : (
              "Update Assessment Scale"
            )}
          </Button>
        </div>
      )}
    </>
  );
};

export default LevelsAssessmentScale;
